/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STORE_AVAILABILITY_REFINEMENT } from '@/extensions/bopis/constants';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Plus, Minus } from 'lucide-react';
import { useStoreLocator } from '@/extensions/store-locator/providers/store-locator';

interface RefineInventoryProps {
    isFilterSelected: (attributeId: string, value: string) => boolean;
    hasActiveFilter: (attributeId: string) => boolean;
    toggleFilter: (attributeId: string, value: string) => void;
    /**
     * Rail chrome, threaded from `CategoryRefinements` so this group wears the
     * same frame as every other group beside it. See the note on the
     * Collapsible below for why it used to be boxed. Defaults to `card`.
     */
    variant?: 'card' | 'rail';
}

/**
 * RefineInventory Component
 *
 * Displays an "Availability" filter that allows users to filter products
 * by availability at a selected store. Integrates with the store locator feature.
 *
 * THE HEADING IS "GET IT FAST", NOT "AVAILABILITY", AND THAT IS A CORRECTION.
 * An earlier revision of this comment read live's `Availability` heading onto
 * THIS group and cited it confidently on both brands. The citations were real;
 * the identification was wrong, and it only became visible once the sandbox
 * grew the catalogue refinement that live's Availability heading actually
 * belongs to.
 *
 * WHAT SETTLES IT is that live renders BOTH groups on one rail, so they cannot
 * be the same one. `.reference/live-shots/owner-live-nm-plp-getitfast-expanded.png`
 * (live NM PLP, the owner's own browser, 2574px wide) shows, in rail order:
 * Category, `Get it Fast (1)`, Designer, Size, Color, Price, Type, Occasion,
 * and then `Availability` EXPANDED over a single `In-Stock (4457)` checkbox.
 * The Selected Filters box at the top of that same frame carries the chip
 * `Get it Fast: Store Pickup`.
 *
 *   live's Availability  = stock status. In-Stock / Preorder. On BG's dresses
 *                          PLP, expanded in
 *                          live-shots/live-bg-plp-1280-facets-expanded.png
 *                          (2560x46636, 12 groups, Availability last), it
 *                          reads `In-Stock (1916)` / `Preorder (12)`.
 *                          THAT IS OUR `c_preOrder` GROUP, which renders those
 *                          two labels verbatim — not this component.
 *   live's Get it Fast   = store pickup and delivery speed
 *                          (`data-tid="filter-key-In Store"`, values
 *                          csp/sdd/ndd). Its Store Pickup value is what THIS
 *                          component filters on, via the
 *                          `c_availableStores` refinement (`ilids` is not in
 *                          the SCAPI contract -- see the constant's docstring).
 *
 * So this group wore the other group's name, and once `c_preOrder` started
 * coming back from the sandbox every rail rendered the word "Availability"
 * twice. Renaming is the fix that keeps the control: deleting it would take
 * out the only caller of the store-availability refine and the only trigger for
 * the product tile's pickup indicator.
 *
 * WHAT IS STILL UNMEASURED, so nobody re-derives it as settled:
 *   - BG has no Get it Fast facet in any capture on disk, and no store-pickup
 *     facet under any name — its dresses rail is 12 groups with none, and its
 *     search rail is 8 with none. So on BG this group is `[ours-extra]`
 *     whatever it is called, and the label here is NM's, inherited rather than
 *     measured. A BG capture showing a store-pickup facet would change it.
 *   - live's Get it Fast is BROADER than this control: it carries same-day and
 *     next-day delivery alongside Store Pickup, and we render only the pickup
 *     arm. The group name is still right for a group containing that arm.
 *   - the row label below stays "In stock at {{storeName}}" — live's chip says
 *     "Store Pickup", but live's own row text inside the expanded group is not
 *     captured, and the store name is information the chip does not carry.
 *
 * When a store-availability refine is already active, its value stays in sync with the globally
 * selected store no matter where the store locator was opened. Opening the locator from
 * this row (checkbox with no store, or store name) turns the refine on after the next
 * store selection; if the refine was already off and the locator was opened elsewhere,
 * changing the store does not enable the refine.
 *
 * @param isFilterSelected - Function to check if a filter is currently selected
 * @param hasActiveFilter - Whether any refine is active for an attribute (from parent `effectiveRefines`)
 * @param toggleFilter - Function to toggle a filter on/off
 * @returns ReactElement
 */
export default function RefineInventory({
    isFilterSelected,
    hasActiveFilter,
    toggleFilter,
    variant = 'card',
}: RefineInventoryProps) {
    const { t } = useTranslation('extBopis');
    const isRail = variant === 'rail';

    // Get selected store info to display name and use storeId for filtering
    const selectedStoreInfo = useStoreLocator((s) => s.selectedStoreInfo);
    const openStoreLocator = useStoreLocator((state) => state.open);
    const isStoreLocatorOpen = useStoreLocator((state) => state.isOpen);
    const enableInventoryFilterOnNextStorePickRef = useRef(false);

    const storeId = selectedStoreInfo?.id || '';
    const storeIdRef = useRef<string>(storeId);
    // Use storeId directly for checked state, not the ref (ref is for tracking changes)
    const isChecked = isFilterSelected(STORE_AVAILABILITY_REFINEMENT, storeId);
    const hadStoreRefine = hasActiveFilter(STORE_AVAILABILITY_REFINEMENT);

    useEffect(() => {
        if (!isStoreLocatorOpen) {
            enableInventoryFilterOnNextStorePickRef.current = false;
        }
    }, [isStoreLocatorOpen]);

    // Keep the store refine aligned with the globally selected store whenever the filter is already on (any locator entry).
    // If the locator was opened from this component's checkbox (no store) or store link, the next store pick
    // also turns the filter on even when it was off.
    useEffect(() => {
        const prevInv = storeIdRef.current;
        const storeChanged = prevInv !== storeId;

        if (!storeId) {
            storeIdRef.current = storeId;
            return;
        }

        if (enableInventoryFilterOnNextStorePickRef.current && storeChanged) {
            enableInventoryFilterOnNextStorePickRef.current = false;
            if (!isChecked) {
                toggleFilter(STORE_AVAILABILITY_REFINEMENT, storeId);
            }
            storeIdRef.current = storeId;
            return;
        }

        if (storeChanged && hadStoreRefine && !isChecked) {
            toggleFilter(STORE_AVAILABILITY_REFINEMENT, storeId);
        }

        storeIdRef.current = storeId;
    }, [storeId, toggleFilter, hadStoreRefine, isChecked]);

    const handleCheckboxChange = () => {
        if (storeId) {
            // Store is selected, toggle the filter
            toggleFilter(STORE_AVAILABILITY_REFINEMENT, storeId);
        } else {
            // No store selected, open the store locator
            enableInventoryFilterOnNextStorePickRef.current = true;
            openStoreLocator();
        }
    };

    const handleStoreNameClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();
        enableInventoryFilterOnNextStorePickRef.current = true;
        openStoreLocator();
    };

    const storeLinkText = selectedStoreInfo?.name || t('storeInventoryFilter.selectStore');
    /**
     * COLLAPSED ON FIRST PAINT. The previous `true` was carried forward as
     * "match previous behavior" and had no evidence under it; live shuts this
     * group on both brands, on both surfaces, at every width captured.
     *
     *   live BG  the owner's own browser shot of
     *            bergdorfgoodman.com/search/?N=0&Ntt=dress at 1728
     *            (.reference/live-shots/owner-live-1728-header.png, address bar
     *            legible in the frame) shows CATEGORY carrying a minus and all
     *            seven other groups — DESIGNER, SIZE, COLOR, PRICE,
     *            AVAILABILITY, SALE, GENDER — carrying a plus. The decompiled
     *            captures agree and bound it: bg-search-d-{768,1024,1280,1440}
     *            and bg-search-facet-d-1024 emit `role="tabpanel"` exactly once
     *            each (2 raw, halved), i.e. one expanded group per page, and
     *            the one expanded is Color, Category, Designer, Category,
     *            Designer respectively.
     *   live NM  the served markup expands TWO — Category and Get it Fast — and
     *            shuts the other seven, this group among them. Discriminator:
     *            the group header's chevron span, `..._status_expanded`
     *            (title `Collapse X`) against `..._status_collapsed` (title
     *            `Expand X`). Raw dump `dumps/nm-search-d-1280`: Collapse at
     *            byte 272188 (Category) and 277479 (Get it Fast); Expand at
     *            279348/279896/280436/280971/281508/282066/282614 — Gender,
     *            Designer, Size, Color, Price, AVAILABILITY, Sale. The
     *            decompiled frames agree at 1024, 1280, 1440 and 1920.
     *
     * SO THE EXPANSION RULE IS PER BRAND, NOT SHARED — an earlier revision of
     * this comment read live NM as "one open, seven shut" and generalised it to
     * "ONE expanded group, never two". That was a bounding error: it counted
     * only the seven `filter-key-*` groups and missed Category, which NM renders
     * through a different control (`button.search-results__cat-button`,
     * `aria-expanded="true"`) and which is expanded. BG's invariant really is
     * exactly one; NM's is two.
     *
     * It does not change what this group does, because NM's second expanded
     * group is GET IT FAST — a store-pickup / delivery-speed facet
     * (`data-tid="filter-key-In Store"`, checkbox values csp/sdd/ndd) that this
     * storefront does not render and SCAPI does not return. Availability is shut
     * on live NM, so `false` here is NM's own value, not BG's borrowed.
     *
     * IT ALSO BROKE THE RAIL'S STICKY PIN, which is the reason this is not
     * merely cosmetic: the pin offset is `min(header, 100vh - railHeight)`, so
     * every group that opens on first paint pushes that term further negative.
     */
    const [isOpen, setIsOpen] = useState(false);

    return (
        /* THE BOX WAS FRAMEWORK CHROME, NOT DESIGN (measurement-discipline
           rule 4). This group hardcoded the stock
           bordered-accordion card at every variant, so on NM's rail — where
           every sibling group is separated by a single 1px divider — the
           availability group was the ONLY boxed one. Measured on the running
           app at 768/1024/1280: siblings 1 bordered side, this group 4.

           Live boxes it on NEITHER brand. NM hangs a bottom divider
           (`.faceted-left-nav__filter{border-bottom:1px solid #d3d3d3}`,
           app-search.*.bundle.css:1 offset 238460 — capture `border-b` with
           `borderBottomColor: rgb(211,211,211)` at search-d-1280/App.tsx:632
           and search-d-1024/App.tsx:592); BG hangs a top divider instead
           (`.rc-collapse>.rc-collapse-item{border-top:1px solid #d3d3d3}`,
           bg client bundle offset 538360). Same colour, opposite edge.

           Only the `rail` arm is corrected here. BG mounts `card`, where a box
           on this group matches the box on every group beside it — so BG stays
           internally consistent, and the card-to-divider change BG needs to
           match live is the whole-rail variant switch that is held behind the
           `--facet-*` extraction. Filed, not smuggled in. */
        <Collapsible
            open={isOpen}
            onOpenChange={setIsOpen}
            className={isRail ? 'border-b border-(--facet-divider)' : 'border border-border mb-4 rounded-ui'}
            data-testid="sf-store-inventory-filter">
            {/* THE HEADING IS A BARE h3 + BARE span, WHICH IS THE SIBLINGS'
                MECHANISM — see the group header in
                `components/category-refinements/index.tsx`. It is deliberately
                NOT a `Typography` variant, and that is the whole of this fix.

                `Typography variant="small"` expands to `text-sm font-medium
                leading-none`. On the WRAPPER that was harmless: `cn()` runs
                tailwind-merge and the `text-(length:--facet-item-font-size)`
                className below wins the font-size group. On the PAINTING SPAN
                it was not — the span carried no size class of its own, so
                `text-sm` landed and STOPPED the span inheriting the token.

                Measured on the running app, NM search rail, clientWidth 1440,
                guarded on href + clientWidth + data-brand:

                    Get it Fast span   14px / 500 / lh 14px   <- Typography
                    Gender, Designer,
                    Size, Color, Price
                    span               16px / 400 / lh 24px   <- inherits

                NM's `--facet-item-font-size` is STEPPED (14px, 16px in the xl
                block of `tokens/brand-nm/search.css`), so the gap only opens
                at >=1280 — which is exactly the desktop SRP the divergence was
                reported against. At 390 the token is 14px, so the size matched
                by coincidence while the WEIGHT and LINE-HEIGHT still did not.
                That coincidence is why a hardcoded 16px would have been the
                same defect one step later: it would re-break below xl.

                Inheriting is what makes this drift-proof. The span now has no
                type of its own on either brand, at any width, so it cannot
                disagree with the rail it sits in.

                The non-rail arm keeps `Typography`'s type utilities (`text-sm
                font-medium`, plus its own `leading-normal`) so it PAINTS the
                same. It is not byte-identical and the snapshots record the
                difference: tailwind-merge ordered the classes differently, and
                `text-left` is gone from both the h3 and the span because it
                came from `typographyVariants`' align default.

                Dropping it is inert, and the sibling group is the control.
                Measured on the running rail at 1440: the trigger BUTTON between
                them carries a literal `text-left`, so it computes `left` and
                the span inherits `left` whether or not the span declares it;
                the h3 computes `start` either way, and every glyph it contains
                is inside the button. After this change Get it Fast's h3, button
                and span carry class strings byte-identical to the sibling's,
                with identical computed `text-align`.

                The card arm is currently unreachable in the app — BG is the
                only `card` rail (`SEARCH_UX_BY_BRAND` / `PLP_UX_BY_BRAND`) and
                BG never mounts this group at all
                (`INJECTED_GROUP_SLOT.bg === 'none'`) — but the Storybook `card`
                story renders it. Moving it onto the siblings' own card heading
                (`type-label-large`) is a separate change needing its own
                evidence, so it is deliberately not made here. */}
            <h3
                className={
                    isRail
                        ? 'flex h-(--facet-group-header-height) items-center text-(length:--facet-item-font-size) transition-colors hover:bg-muted/60'
                        : 'text-sm font-medium leading-normal p-4 transition-colors hover:bg-muted/60'
                }>
                <CollapsibleTrigger className="flex items-center justify-between w-full text-left px-1 py-1 -mx-1 cursor-pointer">
                    <span className={isRail ? undefined : 'text-sm font-medium leading-none'}>
                        {t('storeInventoryFilter.heading')}
                    </span>
                    {isOpen ? <Minus className="size-4" /> : <Plus className="size-4" />}
                </CollapsibleTrigger>
            </h3>
            <CollapsibleContent className={isRail ? 'pb-4 text-(length:--facet-item-font-size)' : 'px-4 pb-4'}>
                <div className="flex items-start space-x-2 p-2">
                    <Checkbox
                        id="inventory-filter"
                        checked={isChecked}
                        onCheckedChange={handleCheckboxChange}
                        aria-label={t('storeInventoryFilter.checkboxAriaLabel', { storeName: storeLinkText })}
                        data-testid="sf-store-inventory-filter-checkbox"
                        className="size-4"
                    />
                    <label htmlFor="inventory-filter" className="text-sm font-medium leading-none cursor-pointer">
                        {t('storeInventoryFilter.label', { storeName: ' ' })}
                        <span
                            className="underline cursor-pointer hover:opacity-70"
                            onClick={handleStoreNameClick}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleStoreNameClick(e);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label={
                                selectedStoreInfo
                                    ? t('storeInventoryFilter.changeStore')
                                    : t('storeInventoryFilter.selectStore')
                            }>
                            {storeLinkText}
                        </span>
                    </label>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}
